import React, { useState } from 'react';
import { Truck, Eye, EyeOff, Loader, CheckCircle, ArrowLeft } from 'lucide-react';
import api from '../../utils/api';

const BL = '#0D2B6B', YW = '#F5C400';

export default function LoginPage({ onLogin }) {
  const [mode, setMode]         = useState('login'); // 'login' | 'forgot' | 'forgot_sent'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const login = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { email, password });
      localStorage.setItem('apex_token', res.data.token);
      localStorage.setItem('apex_admin_name', res.data.name || 'Admin');
      onLogin();
    } catch (err) {
      setError(err.response?.status === 401
        ? 'Incorrect email or password. Try again.'
        : 'Could not connect to server. Make sure the backend is running.');
    } finally { setLoading(false); }
  };

  const sendForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      setMode('forgot_sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Check the email address.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${BL} 0%, #1a3f8f 100%)` }}>
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-10" style={{ background: YW }} />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full opacity-5" style={{ background: YW }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl"
            style={{ backgroundColor: YW }}>
            <Truck className="w-8 h-8" style={{ color: BL }} />
          </div>
          <h1 className="text-3xl font-black text-white">Apex Towing</h1>
          <p className="text-white/50 text-sm mt-1">Admin Panel</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <>
              <h2 className="text-xl font-black mb-1" style={{ color: BL }}>Welcome back</h2>
              <p className="text-gray-400 text-sm mb-6">Sign in to your admin panel</p>

              <form onSubmit={login} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="admin@apextowing.com.au"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500 focus:ring-1 focus:ring-apex-blue-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500 focus:ring-1 focus:ring-apex-blue-500"
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading || !email || !password}
                  className="w-full py-3.5 rounded-xl font-black text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: BL, color: 'white' }}>
                  {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Sign In'}
                </button>
              </form>

              <div className="text-center mt-4">
                <button onClick={() => { setMode('forgot'); setError(''); }}
                  className="text-sm font-semibold hover:underline" style={{ color: BL }}>
                  Forgot password?
                </button>
              </div>
            </>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {mode === 'forgot' && (
            <>
              <button onClick={() => { setMode('login'); setError(''); }}
                className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm mb-5">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
              <h2 className="text-xl font-black mb-1" style={{ color: BL }}>Reset Password</h2>
              <p className="text-gray-400 text-sm mb-6">Enter your admin email — we'll send a reset link.</p>

              <form onSubmit={sendForgotPassword} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1.5">Admin Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    placeholder="admin@apextowing.com.au"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>
                )}

                <button type="submit" disabled={loading || !email}
                  className="w-full py-3.5 rounded-xl font-black text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: BL }}>
                  {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}

          {/* ── FORGOT SENT ── */}
          {mode === 'forgot_sent' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-black mb-2" style={{ color: BL }}>Check Your Email</h2>
              <p className="text-gray-500 text-sm mb-2">A password reset link has been sent to:</p>
              <p className="font-bold text-gray-800 mb-6">{email}</p>
              <p className="text-xs text-gray-400 mb-6">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
              <button onClick={() => { setMode('login'); setError(''); }}
                className="w-full py-3 rounded-xl font-bold text-white" style={{ backgroundColor: BL }}>
                Back to Login
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-white/30 text-xs mt-6">
          © {new Date().getFullYear()} Apex Towing Solutions · Brisbane, QLD
        </p>
      </div>
    </div>
  );
}